import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField
} from '@mui/material';
import {useSnackbar} from "notistack";

interface CopyDialogContextType {
    openCopyDialog: (title: string, description: string, text: string) => void;
}

const CopyDialogContext = createContext<CopyDialogContextType | null>(null);

interface DialogState {
    isOpen: boolean;
    title: string;
    description: string;
    text: string;
}

interface CopyDialogRendererContextType {
    dialogState: DialogState | null;
    handleClose: () => void;
    handleCopy: () => Promise<void>;
}

const CopyDialogRendererContext = createContext<CopyDialogRendererContextType | null>(null);

export function CopyDialogProvider({children}: {
    children: ReactNode,
}) {
    const [dialogState, setDialogState] = useState<DialogState | null>(null);
    const {enqueueSnackbar} = useSnackbar();

    const openCopyDialog = useCallback((title: string, description: string, text: string) => {
        setDialogState({
            isOpen: true,
            title,
            description,
            text,
        });
    }, []);

    const handleClose = useCallback(() => {
        setDialogState(null);
    }, []);

    const handleCopy = useCallback(async () => {
        if (dialogState) {
            await navigator.clipboard.writeText(dialogState.text);
            enqueueSnackbar('Copied to clipboard', {variant: 'success'});
            setDialogState(null);
        }
    }, [dialogState, enqueueSnackbar]);

    const contextValue = useMemo(() => ({openCopyDialog}), [openCopyDialog]);
    const rendererContextValue = useMemo(
        () => ({dialogState, handleClose, handleCopy}),
        [dialogState, handleClose, handleCopy],
    );

    return (
        <CopyDialogContext value={contextValue}>
            <CopyDialogRendererContext value={rendererContextValue}>
                {children}
            </CopyDialogRendererContext>
        </CopyDialogContext>
    );
}

export function CopyDialogRenderer() {
    const context = useContext(CopyDialogRendererContext);
    if (!context) {
        throw new Error('CopyDialogRenderer must be used within a CopyDialogProvider');
    }
    const {dialogState, handleClose, handleCopy} = context;

    if (!dialogState) return null;

    return createPortal(
        <Dialog open={dialogState.isOpen} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>{dialogState.title}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 2}}>
                    {dialogState.description}
                </DialogContentText>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <TextField
                        autoFocus
                        multiline
                        rows={6}
                        fullWidth
                        variant="outlined"
                        value={dialogState.text}
                        slotProps={{input: {readOnly: true}}}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={handleClose}>Close</Button>
                <Button onClick={handleCopy} variant="contained" color="primary">
                    Copy
                </Button>
            </DialogActions>
        </Dialog>,
        document.body,
    );
}

export function useCopyDialog({title, description}: {
    title: string,
    description: string,
}) {
    const context = useContext(CopyDialogContext);
    if (!context) {
        throw new Error('useCopyDialog must be used within a CopyDialogProvider');
    }

    return useCallback(
        (text: string) => context.openCopyDialog(title, description, text),
        [context, title, description]
    );
}
